import { useSubscription } from "../context/SubscriptionContext";
import { useClerk } from "@clerk/react";

const PRO_FEATURES = [
  { icon: "∞", label: "Unlimited searches" },
  { icon: "📋", label: "Up to 200 results per search" },
  { icon: "◎", label: "Full data: 2016 – present" },
  { icon: "🔍", label: "All conflict and POD banners with full detail" },
  { icon: "🏦", label: "Ownership & research modules included" },
  { icon: "🖨", label: "Full PDF profile printing" },
  { icon: "⚖", label: "Provider and company comparisons" },
  { icon: "🔔", label: "Watch list and payment threshold email alerts" },
  { icon: "🚨", label: "OIG + SAM exclusion alerts" },
  { icon: "⚠", label: "POD risk detection and alerts (OIG Work Plan 2026)" },
  { icon: "📊", label: "Pro-limited chart and report CSV exports (500 rows)" },
];

const ENTERPRISE_FEATURES = [
  { icon: "📂", label: "Import your own provider lists" },
  { icon: "🔎", label: "Build custom filtered lists — slice by specialty, state, payment amount, conflict flags, POD risk, and more" },
  { icon: "◉", label: "Apply your provider list as a filter across all search and analytics tools" },
  { icon: "📊", label: "Compare custom provider lists side-by-side with conflict analysis" },
  { icon: "🏭", label: "Import your vendor list and match it to CMS Open Payments companies" },
  { icon: "⚑", label: "Detect conflict-of-interest exposure between your vendors and your providers" },
  { icon: "∞", label: "Unlimited CSV exports — no row caps" },
  { icon: "🏢", label: "Multi-seat team access" },
];

function EnterpriseCard() {
  return (
    <div className="bg-navy-900 border border-purple-500/30 rounded-xl p-5 flex flex-col">
      <div className="text-center mb-4">
        <div className="text-purple-300 text-xs font-medium uppercase tracking-wider mb-1">Enterprise</div>
        <div className="text-2xl font-display text-white font-bold">Custom pricing</div>
        <div className="text-white/40 text-xs mt-1">Annual contract · team access included</div>
        <div className="text-purple-300/60 text-xs mt-2 font-medium">All Pro features, plus:</div>
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {ENTERPRISE_FEATURES.map((f) => (
          <li key={f.label} className="flex items-start gap-3 text-sm">
            <span className="text-purple-300 w-5 text-center shrink-0 mt-0.5">{f.icon}</span>
            <span className="text-white/80">{f.label}</span>
          </li>
        ))}
      </ul>
      
      <a href="mailto:support@providerpaywatch.com?subject=Enterprise%20Pricing%20Inquiry"
        target="_blank"
        rel="noreferrer"
        className="w-full text-center py-2.5 px-4 rounded-lg border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 transition text-sm font-medium block"
      >
        Contact us — support@providerpaywatch.com
      </a>
    </div>
  );
}

const DEFAULT_MESSAGE = "You've reached your search limit. Upgrade to continue.";

export default function UpgradeModal() {
  const {
    showUpgradeModal,
    setShowUpgradeModal,
    showSignInModal,
    setShowSignInModal,
    upgradeToTier,
    upgradeMessage,
    setUpgradeMessage,
    upgradeFocusTier,
    setUpgradeFocusTier,
    tier,
  } = useSubscription();

  const { openSignIn } = useClerk();

  // ── Sign-in modal for anonymous users ────────────────────────────────────

  if (showSignInModal) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: "rgba(8,15,28,0.85)", backdropFilter: "blur(4px)" }}
      >
        <div className="bg-navy-800 border border-white/10 rounded-2xl w-full max-w-md p-6 sm:p-7 relative">
          <button
            onClick={() => setShowSignInModal(false)}
            className="absolute top-4 right-4 text-white/30 hover:text-white/70 text-xl leading-none"
          >
            ×
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1.5 bg-gold-500/10 border border-gold-500/30 text-gold-400 text-xs font-medium px-3 py-1 rounded-full mb-4">
              <span>✦</span> Free Account
            </div>
            <h2 className="font-display text-2xl text-white mb-2">
              Create a free account
            </h2>
            <p className="text-white/50 text-sm">
              You've used your 5 guest searches. Create a free account for 10 searches per day — or upgrade to Pro for unlimited searches and full access.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {/* Free */}
            <div className="bg-navy-900 border border-white/10 rounded-xl p-3">
              <div className="text-white/50 text-sm font-medium uppercase tracking-wider mb-2">Free</div>
              <ul className="space-y-2">
                {[
                  { icon: "🔍", label: "10 searches/day" },
                  { icon: "📋", label: "Top 10 results" },
                  { icon: "📅", label: "2023–present" },
                ].map((f) => (
                  <li key={f.label} className="flex items-start gap-1.5 text-sm">
                    <span className="text-white/40 w-4 text-center shrink-0">{f.icon}</span>
                    <span className="text-white/60">{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Pro */}
            <div className="bg-navy-900 border border-gold-500/30 rounded-xl p-3 relative">
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                <span className="bg-gold-500 text-navy-900 text-xs font-bold px-2 py-0.5 rounded-full">PRO</span>
              </div>
              <div className="text-gold-400 text-sm font-medium uppercase tracking-wider mb-2">$1,500/mo</div>
              <ul className="space-y-2">
                {[
                  { icon: "∞", label: "Unlimited searches" },
                  { icon: "◎", label: "2016–present" },
                  { icon: "🏦", label: "Ownership & research" },
                  { icon: "✦", label: "14-day trial" },
                ].map((f) => (
                  <li key={f.label} className="flex items-start gap-1.5 text-sm">
                    <span className="text-gold-400 w-4 text-center shrink-0">{f.icon}</span>
                    <span className="text-white/80">{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Enterprise */}
            <div className="bg-navy-900 border border-purple-500/30 rounded-xl p-3">
              <div className="text-purple-300 text-sm font-medium uppercase tracking-wider mb-2">Enterprise</div>
              <ul className="space-y-2">
                {[
                  { icon: "📂", label: "Custom lists" },
                  { icon: "⬆", label: "NPI imports" },
                  { icon: "∞", label: "Unlimited exports" },
                  { icon: "🏢", label: "Team access" },
                ].map((f) => (
                  <li key={f.label} className="flex items-start gap-1.5 text-sm">
                    <span className="text-purple-300 w-4 text-center shrink-0">{f.icon}</span>
                    <span className="text-white/80">{f.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <button
            onClick={() => {
              setShowSignInModal(false);
              openSignIn();
            }}
            className="btn-primary w-full text-center mb-3"
          >
            Sign in / Create free account
          </button>
          <button
            onClick={() => {
              setShowSignInModal(false);
              upgradeToTier("pro");
            }}
            className="w-full text-center py-2 px-4 rounded-lg border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition text-sm font-medium mb-2"
          >
            Start 14-day free trial — $1,500/mo after
          </button>
          <p className="text-center mb-3">
            
            <a href="mailto:support@providerpaywatch.com?subject=Enterprise%20Pricing%20Inquiry"
              target="_blank"
              rel="noreferrer"
              className="text-purple-300/60 hover:text-purple-300 text-sm transition"
            >
              Contact us for Enterprise pricing →
            </a>
          </p>
          <p className="text-center text-white/25 text-xs">
            Already have an account? Signing in will restore your daily searches.
          </p>          
        </div>
      </div>
    );
  }

  // ── Upgrade modal for signed-in users ────────────────────────────────────

  if (!showUpgradeModal) return null;

  function handleClose() {
    setShowUpgradeModal(false);
    setUpgradeMessage(null);
    setUpgradeFocusTier(null);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      style={{ background: "rgba(8,15,28,0.85)", backdropFilter: "blur(4px)" }}
    >
      <div className="bg-navy-800 border border-white/10 rounded-2xl w-full max-w-3xl p-4 sm:p-6 relative max-h-[95vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white/70 text-xl leading-none"
        >
          ×
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 bg-gold-500/10 border border-gold-500/30 text-gold-400 text-xs font-medium px-3 py-1 rounded-full mb-4">
            <span>✦</span> Upgrade Your Plan
          </div>
          <h2 className="font-display text-2xl text-white mb-2">
            Unlock full access
          </h2>
          <p className="text-white/50 text-sm">
            {upgradeMessage || DEFAULT_MESSAGE}
          </p>
        </div>

        {upgradeFocusTier === "enterprise" ? (
          /* Enterprise-only — used when a strictly Enterprise-gated feature (e.g. My Vendors)
             triggers this modal, so Free/Pro users aren't shown an irrelevant Pro upsell. */
          <div className="max-w-sm mx-auto mb-4">
            <EnterpriseCard />
          </div>
        ) : (
          /* Side-by-side tier cards — default view */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start mb-4">

            {/* Pro Plan */}
            <div className="bg-navy-900 border border-gold-500/30 rounded-xl p-5 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-gold-500 text-navy-900 text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  14-DAY FREE TRIAL
                </span>
              </div>
              <div className="text-center mb-4">
                <div className="text-gold-400 text-xs font-medium uppercase tracking-wider mb-1">Pro</div>
                <div className="text-3xl font-display text-white font-bold">
                  $1,500<span className="text-lg text-white/50">/mo</span>
                </div>
                <div className="text-white/40 text-xs mt-1">14-day free trial · cancel anytime</div>
                <div className="text-white/30 text-xs mt-0.5">or $15,000/year (save ~17%)</div>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {PRO_FEATURES.map((f) => (
                  <li key={f.label} className="flex items-start gap-3 text-sm">
                    <span className="text-gold-400 w-5 text-center shrink-0 mt-0.5">{f.icon}</span>
                    <span className="text-white/80">{f.label}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgradeToTier("pro")}
                className="btn-primary w-full text-center mb-2"
              >
                Start free trial — $1,500/mo after
              </button>
              <button
                onClick={() => upgradeToTier("pro_annual")}
                className="w-full text-center py-2 px-4 rounded-lg border border-gold-500/30 text-gold-400 hover:bg-gold-500/10 transition text-sm font-medium"
              >
                Pay annually — $15,000/yr (save ~17%)
              </button>
            </div>

            {/* Enterprise Plan */}
            <EnterpriseCard />

          </div>
        )}

        <p className="text-center text-white/25 text-xs">
          Secure payment via Stripe · No charge during trial
        </p>
      </div>
    </div>
  );
}